import React, { useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import { SearchSelectMulti } from "../../../common/form/SearchSelect/SearchSelectMulti";

const ReviewForm = ({
  activityData,
  onSave,
  onCancel,
  error,
  isSubmitting,
  isNewActivity = false,
  assessmentsData = [],
  assessmentsLoading = false,
}) => {
  // State
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assessmentId: activityData?.hasReviewFor
        ? [{ id: activityData.hasReviewFor.id, name: "Selected Assessment" }]
        : [],
    },
  });

  const watchAssessmentId = watch("assessmentId");

  // Process assessments data when it changes
  useEffect(() => {
    if (assessmentsData && assessmentsData.length > 0) {
      const formattedAssessments = assessmentsData.map((assessment) => {
        const moduleName = assessment.moduleHasAssessment.title || "Untitled Module";
        return {
          id: assessment.id,
          name: `${moduleName}'s Assessment`,
          moduleId: assessment.moduleHasAssessment.id,
          rawData: assessment,
        };
      });
      setAssessments(formattedAssessments);

      // Set default assessment if we have a selected module and no assessment is selected yet
      if (selectedModuleId && (!watchAssessmentId || watchAssessmentId.length === 0)) {
        const defaultAssessment = formattedAssessments.find((a) => a.moduleId === selectedModuleId);
        if (defaultAssessment) {
          setValue("assessmentId", [defaultAssessment]);
        }
      }
    }
  }, [assessmentsData, selectedModuleId, setValue, watchAssessmentId]);

  // Set the selected module ID when editing
  useEffect(() => {
    if (activityData?.moduleHasReviews && activityData.moduleHasReviews[0]?.id) {
      setSelectedModuleId(activityData.moduleHasReviews[0].id);
    } else if (activityData?.parentModuleId) {
      setSelectedModuleId(activityData.parentModuleId);
    }
  }, [activityData]);

  // Set the assessment when editing
  useEffect(() => {
    if (activityData?.hasReviewFor && assessments.length > 0) {
      const assessment = assessments.find((a) => a.id === activityData.hasReviewFor.id);
      if (assessment) {
        setValue("assessmentId", [assessment]);
      }
    }
  }, [activityData, assessments, setValue]);

  // Form submission
  const onSubmit = (data) => {
    if (!data.assessmentId || data.assessmentId.length === 0) {
      return;
    }

    // Create the activity data object with required fields
    const activityData = {
      assessmentId: data.assessmentId[0].id,
      parentModuleId: selectedModuleId,
      type: "Review",
    };

    onSave(activityData);
  };

  // Filter assessments based on search query
  const filterAssessments = async (query) => {
    setSearchQuery(query);
    if (!assessments || assessments.length === 0) return [];

    if (!query) return assessments;

    const lowercaseQuery = query.toLowerCase();
    return assessments.filter((assessment) => assessment.name.toLowerCase().includes(lowercaseQuery));
  };

  const hasAssessments = Array.isArray(assessments) && assessments.length > 0;

  return (
    <FormLayout
      title={`${isNewActivity ? "Create" : "Edit"} Review`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Create" : "Save"}
      disableSubmit={!hasAssessments || !watchAssessmentId || watchAssessmentId.length === 0}
      error={error}>
      <CustomText text="Select Assessment" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomText
        style={styles.subText}
        text="Choose the assessment this review will be associated with"
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />

      {assessmentsLoading ? (
        <CustomText text="Loading assessments..." style={styles.loadingText} useTranslationText={false} />
      ) : !hasAssessments ? (
        <CustomText
          style={styles.errorText}
          text="No assessments available. Please create assessments first."
          useTranslationText={false}
        />
      ) : (
        <SearchSelectMulti
          name="assessmentId"
          control={control}
          label=""
          loading={assessmentsLoading}
          placeholder="Select an assessment"
          backgroundColor="#3E68FE"
          showAllByDefault={true}
          itemType="assessment"
          limitOne={true}
          fetchOptions={filterAssessments}
        />
      )}

      {errors.assessmentId && (
        <CustomText text="Assessment selection is required" style={styles.errorText} useTranslationText={false} />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  loadingText: {
    color: "#667085",
    marginTop: 8,
  },
});

export default ReviewForm;
